// components/LSSF/NewsEventsManagement.jsx
import React, { useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import { MdDelete, MdSearch, MdVisibility, MdEdit, MdAdd } from 'react-icons/md';

const NewsEventsManagement = () => {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [image, setImage] = useState(null);
  const limit = 20;

  const [formData, setFormData] = useState({
    title: '',
    date: '',
    description: ''
  });

  const resetForm = () => {
    setFormData({ title: '', date: '', description: '' });
    setImage(null);
    setIsEditing(false);
    setSelectedItem(null);
  };

  const getNewsEvents = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${base_url}/api/v1/news-events`);
      setData(res.data.data);
      setFilteredData(res.data.data);
      const total = Math.ceil(res.data.data.length / limit);
      setTotalPages(total > 0 ? total : 1);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching news:', err);
      toast.error('Failed to fetch news/events');
      setLoading(false);
    }
  };

  useEffect(() => {
    getNewsEvents();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredData(data);
    } else {
      const searchLower = searchTerm.toLowerCase();
      const filtered = data.filter(item => {
        return (
          (item.title && item.title.toLowerCase().includes(searchLower)) ||
          (item.description && item.description.toLowerCase().includes(searchLower))
        );
      });
      setFilteredData(filtered);
    }
  }, [searchTerm, data]);

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = {
        ...formData,
        ...(image && { image })
      };

      if (isEditing && selectedItem) {
        await axios.put(`${base_url}/api/v1/news-events/${selectedItem.id}`, formDataToSend);
        toast.success('News/Event updated successfully');
      } else {
        await axios.post(`${base_url}/api/v1/news-events`, formDataToSend);
        toast.success('News/Event added successfully');
      }
      resetForm();
      setShowForm(false);
      getNewsEvents();
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Failed to save news/event');
    }
  };

  const deleteItem = async (id) => {
    const choice = window.confirm('Are you sure you want to delete this item?');
    if (choice) {
      try {
        await axios.delete(`${base_url}/api/v1/news-events/${id}`);
        toast.success('News/Event deleted successfully');
        getNewsEvents();
      } catch (err) {
        console.error('Delete error:', err);
        toast.error('Failed to delete item');
      }
    }
  };

  const viewDetails = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const editItem = (item) => {
    setFormData({
      title: item.title || '',
      date: item.date ? item.date.split('T')[0] : '',
      description: item.description || ''
    });
    setImage(item.image || null);
    setSelectedItem(item);
    setIsEditing(true);
    setShowForm(true);
  };

  const paginatedData = filteredData.slice((page - 1) * limit, page * limit);

  return (
    <div className="page-wrapper">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header row d-flex justify-content-between align-items-center">
                <div className="col-md-4">
                  <h4 className="mb-0">News & Events</h4>
                </div>
                <div className="col-md-4">
                  <div className="input-group">
                    <span className="input-group-text"><MdSearch /></span>
                    <input type="text" className="form-control" placeholder="Search by title..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  </div>
                </div>
                <div className="col-md-4 text-end">
                  <button className="btn btn-primary btn-sm" onClick={() => { resetForm(); setShowForm(!showForm); }}>
                    <MdAdd /> {showForm ? 'Close Form' : 'Add News/Event'}
                  </button>
                </div>
              </div>
              <div className="card-body">
                {/* Add/Edit Form */}
                {showForm && (
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">{isEditing ? 'Edit' : 'Add'} News/Event</h5>
                    </div>
                    <div className="card-body">
                      <form onSubmit={handleSubmit}>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Title *</label>
                            <input type="text" className="form-control" name="title" value={formData.title} onChange={handleChange} required />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Date *</label>
                            <input type="date" className="form-control" name="date" value={formData.date} onChange={handleChange} required />
                          </div>
                          <div className="col-md-12 mb-3">
                            <label className="form-label">Description</label>
                            <textarea className="form-control" name="description" value={formData.description} onChange={handleChange} rows="4"></textarea>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Image</label>
                            <input type="file" className="form-control" onChange={handleImageChange} accept="image/*" />
                            {image && <img src={image} alt="Preview" className="mt-2" style={{ maxHeight: '150px' }} />}
                          </div>
                        </div>
                        <div className="text-end">
                          <button type="button" className="btn btn-secondary me-2" onClick={() => { resetForm(); setShowForm(false); }}>Cancel</button>
                          <button type="submit" className="btn btn-primary">{isEditing ? 'Update' : 'Submit'}</button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Data Table */}
                <div className="table-responsive">
                  <table className="table table-bordered shadow-sm mt-3" cellPadding={5}>
                    <thead className="bg-light">
                      <tr>
                        <th>S.No.</th>
                        <th>Image</th>
                        <th>Title</th>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>{[...Array(6)].map((_, i) => (<td key={i}><Skeleton count={1} /></td>))}</tr>
                      ) : paginatedData.length > 0 ? (
                        paginatedData.map((item, index) => (
                          <tr key={item.id}>
                            <td>{(page - 1) * limit + index + 1}</td>
                            <td>
                              {item.image ? <img src={item.image} alt="" style={{ width: '60px', height: '60px', objectFit: 'cover' }} /> : 'No Image'}
                            </td>
                            <td><strong>{item.title}</strong></td>
                            <td>{new Date(item.date).toLocaleDateString()}</td>
                            <td>{item.description?.substring(0, 100)}{item.description?.length > 100 ? '...' : ''}</td>
                            <td>
                              <button className="btn btn-sm btn-info me-1" onClick={() => viewDetails(item)}><MdVisibility size={18} /></button>
                              <button className="btn btn-sm btn-warning me-1" onClick={() => editItem(item)}><MdEdit size={18} /></button>
                              <button className="btn btn-sm btn-danger" onClick={() => deleteItem(item.id)}><MdDelete size={18} /></button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="text-center text-danger"><td colSpan={6}>No news/events found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {filteredData.length > limit && (
                  <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
                    <button className="btn btn-sm btn-outline-secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
                    <span>Page {page} of {totalPages}</span>
                    <button className="btn btn-sm btn-outline-secondary" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Modal */}
      {showModal && selectedItem && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">{selectedItem.title}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                {selectedItem.image && <img src={selectedItem.image} className="img-fluid mb-3 rounded" alt="" />}
                <div className="row">
                  <div className="col-md-6 mb-3"><strong>Date:</strong><p>{new Date(selectedItem.date).toLocaleDateString()}</p></div>
                  <div className="col-md-6 mb-3"><strong>Created:</strong><p>{new Date(selectedItem.created_at).toLocaleString()}</p></div>
                  <div className="col-12 mb-3"><strong>Description:</strong><p className="mt-2 p-3 bg-light rounded">{selectedItem.description || 'No description'}</p></div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default NewsEventsManagement;